import type { ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  FormBadge,
  HomeWeekly,
  RecentPr,
  TodaySession,
  UnitsPreference,
  WeeklyTargetsData,
} from "@jackedlogs/shared";

import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { EmptyState } from "../primitives/EmptyState";
import { Eyebrow } from "../primitives/Eyebrow";
import { makeHomeBoxStyle } from "./glanceCardStyles";
import { HomeHero } from "./HomeHero";
import { FormBadgeCard } from "./FormBadgeCard";
import { RecentPrsStrip } from "./RecentPrsStrip";
import { WeeklyTargetsCard } from "./WeeklyTargetsCard";

// THE Home surface (HR-S4, formerly Plan/PlanView) — one composed tree for
// mobile's Home tab AND the web-rnw console. A real Home now: what you're
// following (carousel / today hero), the glanceable signals (readiness,
// recent PRs, last-7-days stats, quick weigh-in/glucose) and the badges /
// notifications placeholder (S5). Browsing and management moved OUT to the
// Create-hub's Library; the quick-start row retired with them (the hub's
// Nuovo/Ripeti actions own those entries). The host owns the scroll
// container (mobile adds pull-to-refresh) and mounts the data-heavy cards
// as slots since their wiring differs per platform; hero / badge / PRs /
// weekly are pure props so they render identically.
export function HomeView({
  theme,
  today,
  resolveName,
  heroLoading,
  isStartingToday,
  onStartToday,
  onNewWorkout,
  formBadge,
  weekly,
  weeklyTargets,
  onOpenProgress,
  hasAthleteTargets,
  onSetGoals,
  prs,
  units,
  locale,
  programHighlightSlot,
  quickWeightSlot,
  quickGlucoseSlot,
}: {
  theme: UiTheme;
  // ——— Home hero ———
  today: TodaySession | null;
  resolveName: (slug: string, snapshot?: string) => string;
  heroLoading: boolean;
  isStartingToday: boolean;
  onStartToday: () => void;
  /** The blank hero's primary CTA (host nav / seam — web hints log-on-mobile). */
  onNewWorkout: () => void;
  // ——— Glanceable signals ———
  formBadge: FormBadge | null;
  /** Rolling last-7-days stats (shared computeHomeWeekly); null hides the card. */
  weekly: HomeWeekly | null;
  /** AP-3 three-zone meter data (shared computeWeeklyTargets); null hides it. */
  weeklyTargets: WeeklyTargetsData | null;
  /** Badge + PRs + weekly tap target → Progress (host nav / seam). */
  onOpenProgress: () => void;
  /** ME-6 — has the athlete set any weekly targets? `false` swaps the meter for
   *  the "set your goals" empty-state; undefined (loading) keeps the meter. */
  hasAthleteTargets?: boolean;
  /** ME-6 — host-injected CTA for the empty-state (Me/Obiettivi lands in ME-1);
   *  omit ⇒ the invite shows without a button, never a broken one. */
  onSetGoals?: () => void;
  prs: RecentPr[];
  units: UnitsPreference;
  locale: string;
  // ——— Host-mounted sections (platform-specific wiring) ———
  /** The followed-programs carousel (host-wired: mobile starts on the logger,
   *  web is management-only). When provided it REPLACES the HomeHero — the
   *  carousel owns the "what do I train next" slot. Omit (undefined) when
   *  there are no followed programs so the blank HomeHero shows instead. */
  programHighlightSlot?: ReactNode;
  quickWeightSlot: ReactNode;
  /** Quick glucose entry (slice A). Optional — host omits where unused. */
  quickGlucoseSlot?: ReactNode;
}) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const styles = makeStyles(theme);

  // The "what to train next" headline: followed-programs carousel when there
  // are any (it owns day selection + run + manage), otherwise the action-first
  // blank/today hero. Full-width on both layouts. The wrapper draws the ONE
  // box (same idiom as every other Home card) around WHICHEVER variant is in
  // the slot — the variants themselves are chrome-less, so they can't drift
  // apart or double-box (HR-9).
  const hero = (
    <View style={styles.heroBox}>
      {programHighlightSlot ?? (
        <HomeHero
          today={today}
          resolveName={resolveName}
          theme={theme}
          loading={heroLoading}
          isStarting={isStartingToday}
          onStartToday={onStartToday}
          onNewWorkout={onNewWorkout}
        />
      )}
    </View>
  );

  // The glanceable pieces, composed differently per layout below.
  const readiness = (
    <FormBadgeCard badge={formBadge} theme={theme} onPress={onOpenProgress} />
  );
  // AP-3 three-zone meter — now the ONE "this week" card: it folds the rolling
  // last-7-days workout count + volume into its collapsed header (WeeklyStatsCard
  // retired, AP-1b-u) and scores tonnage + exercise targets, not just hard sets.
  // Sits right after readiness. Null (nothing trained + no targets) hides it.
  const targetsCard = (
    <WeeklyTargetsCard
      data={weeklyTargets}
      theme={theme}
      units={units}
      locale={locale}
      weekly={weekly}
      resolveExerciseName={resolveName}
      onOpenProgress={onOpenProgress}
      hasAthleteTargets={hasAthleteTargets}
      onSetGoals={onSetGoals}
    />
  );
  const prsStrip = (
    <RecentPrsStrip
      prs={prs}
      resolveName={resolveName}
      theme={theme}
      units={units}
      locale={locale}
      onPress={onOpenProgress}
    />
  );

  // The "Badges" group closes the Home (HR-9b, Antenore 2026-07-04): the PR
  // gallery quadratone as its first element, then the News box that will host
  // system news/achievements (HR-5) — for now a factual empty state inside
  // the standard Home box. This replaces the old bare "Badges and news"
  // placeholder. No outer wrapper card: the group is a header + siblings
  // (card-in-card nesting is banned).
  const badgesGroup = (
    <View>
      <Eyebrow theme={theme} style={styles.groupTitle}>
        {t("mobile.home.badgesTitle", { defaultValue: "Badges" })}
      </Eyebrow>
      {prsStrip}
      <View style={styles.newsBox}>
        <EmptyState
          theme={theme}
          icon="inbox"
          title={t("mobile.home.newsTitle", { defaultValue: "News" })}
          sub={t("mobile.home.newsEmpty", {
            defaultValue: "App news will show up here.",
          })}
        />
      </View>
    </View>
  );

  // Wide (desktop console): two columns from the top so every left edge lines
  // up. MAIN (left) is the content stream — hero/carousel, the week's
  // numbers, then the Badges group (PR gallery + News) closing the column.
  // RAIL (right) is the act-now stream — readiness flag + quick entries.
  // Each child owns its own horizontal margin/padding (s5). Mobile is always
  // narrow → single-scroll branch below.
  if (wide) {
    return (
      <View style={styles.cols}>
        <View style={styles.main}>
          {hero}
          {targetsCard}
          {badgesGroup}
        </View>
        <View style={styles.rail}>
          {readiness}
          {quickWeightSlot}
          {quickGlucoseSlot}
        </View>
      </View>
    );
  }

  // Narrow (phone / narrow browser): one scroll, top to bottom; the Badges
  // group (PR gallery + News) closes the page after the quick entries
  // (Antenore 2026-07-04).
  return (
    <>
      {hero}
      {readiness}
      {targetsCard}
      {quickWeightSlot}
      {quickGlucoseSlot}
      {badgesGroup}
    </>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    // The one hero box: same idiom as every Home card, plus surface fill and
    // an s4 top margin — the hero is the first element under the tab header
    // and was gluing to it (Antenore 2026-07-04).
    heroBox: {
      ...makeHomeBoxStyle(theme),
      backgroundColor: p.surface,
      marginTop: s.s4,
    },
    // Group header for the closing Badges section — the shared <Eyebrow>
    // voice (ink3 sm 700 +0.4) plus this group's own s5 gutter.
    groupTitle: {
      marginHorizontal: s.s5,
      marginBottom: s.s2,
    },
    // News lives in the standard Home box (HR-5 will fill it).
    newsBox: {
      ...makeHomeBoxStyle(theme),
      backgroundColor: p.surface,
    },
    // Wide console 2-column split. No gap: each child carries its own
    // horizontal margin/padding (s5), so column content keeps the same edge
    // rhythm as the full-width hero above. flex-start so the columns don't
    // stretch to equal height. The rail caps at 380 so its glanceable cards
    // don't balloon on ultra-wide screens; main takes the rest. paddingTop
    // aligns the rail's first card with the hero box's s4 top margin.
    cols: { flexDirection: "row", alignItems: "flex-start" },
    main: { flex: 2, minWidth: 0 },
    rail: { flex: 1, minWidth: 0, maxWidth: 380, paddingTop: s.s4 },
  });
}
